/**
 * The matrix, drawn — the KANON five states, the columns they sit in, and every number formatted.
 *
 * `entities/plan-draft` answers what a cell HOLDS (`buildMatrix`). This answers what it LOOKS LIKE,
 * which is a different question with its own rules, and keeping them apart is what lets the engine
 * be replayed against a backend fixture without a locale anywhere near it.
 *
 * ── The five states, from `KANON/modules/prog-matrix.css` ────────────────────────────────────
 * · `base`     — the authored column's own value. Neutral ink; the ring and the ŹRÓDŁO tag come
 *                from `isBase`, which is true for the whole first column whatever its state.
 * · `certain`  — the engine computed it and it will be exactly this. Green, `--matrix-ink`.
 * · `estimate` — nothing is knowable yet. Violet, italic, dashed. Two causes, and they are
 *                different: a DYNAMIC rule resolves at workout start (R2), and a LADDER row's load
 *                arrives from the logged top mid-session (R11).
 * · `manual`   — the trainer pinned it. The accent, and the only accent on the surface.
 * · `flat`     — nothing progresses this row. Tertiary ink. A warm-up, a rule with no delta, a row
 *                carrying nothing on the active axis.
 *
 * `isBase` is a separate flag rather than a sixth state because the canon composes them:
 * `s-base s-flat` and `s-base s-estimate` are both drawn, and `s-base` alone is what a certain row's
 * authored cell gets. One class carries the ring, the other carries the colour.
 *
 * ── Two deliberate departures from the canon's illustrative data ─────────────────────────────
 * Both are cases where reproducing the mockup's pixels would mean printing a number nothing can
 * stand behind, and the gap report's D4 already records that its arithmetic does not close.
 *
 * 1. **A %1RM cell shows its percent, not kilograms.** The mockup draws "~120 kg / 75 % 1RM", which
 *    is 75% of a client max the matrix does not have. The e1RM read is scoped to the rule bar's
 *    RealMax line (execution ADR G2), and resolving every percent cell through it would put a number
 *    on screen for a library template that has no client at all. The percent is what was authored.
 * 2. **A descent's estimate is derived, and only when there is a top weight to derive it from.** The
 *    mockup shows "~119 kg" under a RealMax top whose own kilograms are equally unknown. Here the
 *    estimate appears when the top's cell carries a weight on the kilogram axis, and the row shows
 *    its bare percent otherwise — R11's "never a fabricated number". The derivation itself is the
 *    backend's own, replayed against `prescription-contract.json`.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. `t` and `locale` arrive as
 * parameters.
 */

import {
  axisForExercise,
  axisForField,
  bandedGateOf,
  buildMatrix,
  carriesBand,
  countsAsWork,
  deriveBackoffWeight,
  findOverride,
  isAmrapSet,
  isEffortAxis,
  measurementValueKey,
  resolvesInSession,
  ruleDelta,
  ruleFor,
  type CycleLifespan,
  type ExerciseDraft,
  type MatrixCell,
  type MatrixRow,
  type ProgressionAxis,
  type SetDraft,
} from '~/entities/plan-draft';
import { measurementFieldDefinition, type MeasurementFieldKey } from '~/shared/measurement';

import {
  cycleIsClosed,
  loggedCellKey,
  type CycleVerdict,
  type LiveMatrixInput,
  type LoggedCell,
} from './executionsToMatrixInputs';

const KEY = 'web:planBuilder.progression';

/** Resolve one locale key, with interpolation. Handed in, never imported. */
export type Translate = (key: string, values?: Record<string, string | number>) => string;

/**
 * How certain a cell's number is. `isBase` rides alongside rather than replacing one of them.
 *
 * `derived` joined the KANON five with the progression port, and it separates two claims the one
 * `estimate` state was making at once. An ESTIMATE is a number this app computed and the backend
 * will compute differently: an execution-gated rule resolves at workout start off what the client
 * did, so the cell shows where the rule is HEADING. A DERIVED cell has no number of its own at all —
 * a back-off descent's load does not exist until the top set is logged (R11), so what the cell
 * carries is the fraction, and nothing about it is a prediction that could be right or wrong.
 *
 * Collapsing them drew a descent in the same violet hedge as a rule that is genuinely guessing, and
 * offered the trainer the same "it might land differently" reading over a row where no arithmetic is
 * being attempted.
 */
export type MatrixCellState = 'base' | 'certain' | 'estimate' | 'derived' | 'manual' | 'flat';

const MATRIX_CELL_STATES: readonly MatrixCellState[] = [
  'base',
  'certain',
  'estimate',
  'derived',
  'manual',
  'flat',
];

export interface MatrixColumnView {
  readonly cycleIndex: number;
  /** "T1" — the cycle's own label, using the plan's unit noun. */
  readonly label: string;
  /** The authored column, which carries the ŹRÓDŁO tag. Exactly one column per matrix. */
  readonly isSource: boolean;
  /** A cycle the client has already consumed. Locked by the server, so locked here (view ADR §8). */
  readonly isLocked: boolean;
  /**
   * The word under the cycle number — the mock's `cycleHeads` sub-line: „BAZA" on the authored
   * column, „ZROBIONY" on a cycle behind the client, „TERAZ" on the one they are standing in, and
   * `null` on everything ahead of them.
   *
   * On a TEMPLATE only the first of the four is ever drawn, which is the template variant this
   * surface has always had: there is no client, so there is nothing behind or ahead of one.
   */
  readonly subLabel: string | null;
  /** The cycle the client is standing in — the column the „TU JEST KLIENT" marker sits over. */
  readonly isNow: boolean;
  /**
   * The two micro-labels over a LIVED column's halves, „ZADANE" | „WYKONANE", or null on a column
   * no cell of which is split. Said once in the head rather than inside every cell.
   */
  readonly halves: { readonly prescribed: string; readonly performed: string } | null;
  /**
   * The label the CHART prints under this cycle, which is the mock's own „C1 · baza" / „C3 · teraz"
   * and is NOT the grid head's: the plot suffixes two columns in lower case and says nothing under
   * a cycle that is merely done. On a template it is the bare label, unchanged.
   */
  readonly chartLabel: string;
}

/**
 * One half of a lived cell: the grid's own column printed large, and its paired column small under
 * it. Owner, 15 September 2026: a lived cell keeps the regular cell's box, so each half carries what
 * an unlived cell's two lines carry and nothing more; the whole row is on `row`.
 */
export interface LoggedHalfView {
  /**
   * The column the grid follows as a bare number, „102,5" on Ciężar, „5" on Powt., the unlived
   * cell's own notation (the caption names the column and its unit). Null where the row has none.
   */
  readonly value: string | null;
  /** The PAIRED column alone, through `setRowText`, „× 5", „× 8–12", „102,5 kg" under Powt. */
  readonly rest: string | null;
  /** Which column `rest` prints, or null where it prints none or the whole row. */
  readonly restField: MeasurementFieldKey | null;
  /** `value` with its unit, „102,5 kg", for the accessible name. Null where `value` is. */
  readonly valueSpoken: string | null;
  /** The row's columns neither line prints, „RPE ≤ 7,5", or null where there are none. */
  readonly others: string | null;
  /** The whole row in one line, for the `title` and the accessible name. */
  readonly row: string;
}

/**
 * One line of the WYKONANE half, compared with what was asked on its own column.
 *
 * ── The ARROW is a display fact and the COLOUR is the backend's ─────────────────────────────
 * `direction` compares the logged number with the target on that column: the band's frozen ceiling
 * where the row carries one on the banded column (10 of 8–12 reads below), the prescription
 * otherwise. It says which way the number moved and nothing more. Whether the row DELIVERED is never
 * read off it: `isShort` is the backend's `shortfallFields` naming this column, so a line can point
 * up and still be red, and this app still never judges a delivery from numbers.
 */
export interface LoggedLineMark {
  /** Null where either number is missing, so there is nothing to compare. */
  readonly direction: 'above' | 'hit' | 'below' | null;
  /** The backend named this column in the row's shortfall. */
  readonly isShort: boolean;
}

/**
 * What a cell of a cycle the client has already LIVED says, two halves side by side, ZADANE on the
 * left and WYKONANE on the right, split by one line.
 *
 * The owner's spec of 15 September 2026, which replaced the prescription stacked over the
 * performance ("dramatycznie zaprojektowane"). The two micro-labels stand once in the column HEAD
 * (`MatrixColumnView.halves`), never inside every cell, and the VERDICT lives on the right half.
 */
export interface LoggedCellView {
  /** What the plan FROZE onto the row at start (R2). Never the template's cell now. */
  readonly prescribed: LoggedHalfView;
  /**
   * What the client did, or null where there are no numbers to print, a session they did not do,
   * one they are standing in, a cancelled one, or a finished one with nothing logged on this row.
   */
  readonly performed: LoggedHalfView | null;
  /**
   * The quiet word standing in the right half where `performed` is null, „w trakcie", „odwołany",
   * „—". Null on a session the client did not do: that half is a red ✗ and nothing else.
   */
  readonly word: string | null;
  /**
   * How the right half is DRAWN, green, red, a missed session's lone ✗, or no claim.
   *
   * It is the whole colour decision, made here, because it composes four separate facts and a
   * component assembling them would be the one place they could be assembled wrongly: the SET's own
   * shortfall (a row short on repetitions holds the whole exercise, R3, whichever column the grid is
   * drawn on), whether the row counts at all (a warm-up is in the record and outside the verdict),
   * whether the client did the session, and whether they are standing in it right now.
   */
  readonly tone: 'delivered' | 'short' | 'missed' | 'neutral';
  /** How the right half's large line compares with what was asked, or null where it prints none. */
  readonly valueMark: LoggedLineMark | null;
  /** The same for the small line, its paired column, judged independently. */
  readonly restMark: LoggedLineMark | null;
  /**
   * A short row with no red line to show it: left un-ticked, or short on a column neither line
   * prints. It carries a ✗ beside the large line so the refusal is never invisible.
   */
  readonly rowShortMark: boolean;
  /**
   * The backend's own reading of the CYCLE this cell sits in, carried down so one answer colours the
   * cell and badges the chart's node. Never recomputed per surface.
   */
  readonly verdict: CycleVerdict;
  /** WHICH absence the name reads out, or `null` where the right half prints a real performance. */
  readonly absence: AbsenceKind | null;
  /** The columns that fell short, in words — „powtórzeniach". Empty where nothing did. */
  readonly shortColumns: readonly string[];
}

export interface MatrixCellView {
  readonly key: string;
  readonly setIndex: number;
  readonly cycleIndex: number;
  readonly state: MatrixCellState;
  readonly isBase: boolean;
  readonly isLocked: boolean;
  /**
   * Whether a click opens the editor. False for a warm-up row, for a row the backend takes no
   * prescription on, and for every consumed cycle.
   *
   * **The base („BAZA") column is editable**, on the owner's call of 15 September 2026, which
   * withdrew the earlier rule that it „belongs to the set table". Mobile has always allowed it and
   * the web now matches: what a C1 edit writes is the row's own prescription rather than a pin, so
   * the whole row re-projects from it. `setMatrixBaseValue` is the writer and carries the reasoning;
   * `isBase` is what tells the two commits apart.
   */
  readonly isEditable: boolean;
  /**
   * WHY a cell refuses the editor, in words, or `null` where it takes one.
   *
   * Two readings and they are different facts: a consumed cycle is behind the client, and a row that
   * does not count toward progression has nothing to prescribe. Enter on a refused cell used to do
   * nothing at all — no editor, no sound, no reason — which reads as a broken grid rather than as a
   * rule. The surface says it out loud through the day dock's live region.
   */
  readonly editRefusal: string | null;
  /** The number, formatted. `'—'` when the row carries nothing on this axis — never `'0'`. */
  readonly text: string;
  /**
   * The same number WITHOUT its unit — "102,5" where `text` says "102,5 kg", "~85" where it says
   * "~85 kg".
   *
   * The canon's full-width drawer prints bare figures; the projection matrix under the set table
   * prints the unit in every cell. One derivation, two renderings — rather than the drawer
   * re-deriving a number this module already formatted, or stripping a suffix back off a string.
   *
   * **Which of the two the drawer picks is `drawerMatrixView`'s decision, not this module's**, and
   * it is not "always bare": the canon's own context line above the grid states no unit, so on a
   * %1RM exercise a bare grid would be unitless everywhere on the surface. That reading is the
   * suffixed one. An earlier revision of this comment claimed the drawer "states the unit once, in
   * the line above the grid" — the canon's copy does not, and that is what left a %1RM grid
   * anonymous.
   */
  readonly value: string;
  /**
   * The mock's `<small>` under the number: what the row prescribes in the columns this grid is NOT
   * about — „× 8–12", „z topu · × 8–12", „5+ · RPE ≤ 9". `null` where the row prescribes nothing
   * outside the grid's own column.
   *
   * It was the STATE in words („autorskie", „bez zmian"), and that was the same claim three times
   * over: the cell's colour, the legend beside the caption and the cell's own accessible name all
   * already carry it, while the one thing the grid could not say was what the client is actually
   * asked to do in that cycle.
   */
  readonly annotation: string | null;
  /** What a screen reader hears — the state in words, since colour is never the only channel. */
  readonly ariaLabel: string;
  /**
   * The value an editor opens on, in the unit the cell is EDITED in — the axis's raw value, or the
   * 0–1 fraction on a ladder row whatever the axis is. `null` for an empty cell.
   */
  readonly editValueRaw: number | null;
  /** True on a ladder row, where the edit is a percent of the top rather than a value on the axis. */
  readonly editsLadderPercent: boolean;
  /**
   * Typed value → stored value: divide by this. 100 on a ladder row and on the %1RM axis, 1
   * elsewhere.
   *
   * It rides on the CELL rather than being derived at the editor from an axis prop, so the matrix
   * component needs no axis at all — which is the point. An axis reaching that component is an axis
   * a caller chose, and the one thing a pin must never take from a caller is its axis (see
   * `MatrixCellAddress`).
   */
  readonly editScale: number;
  /**
   * What the client was GIVEN in this cell and what they did with it, or `null` on every cell of a
   * template and on every cycle the client has not reached.
   *
   * A cell carrying one is drawn SPLIT and takes no editor — the cycle is behind the client, which
   * `isEditable` already refuses for the same reason.
   */
  readonly logged: LoggedCellView | null;
}

export interface MatrixRowView {
  readonly setIndex: number;
  /** The set-type letter, its kind for the badge's colour, and its accessible name. */
  readonly badge: { readonly letter: string; readonly kind: string; readonly label: string };
  /** The row header's accessible name — "Seria 2, robocza". */
  readonly label: string;
  readonly cells: readonly MatrixCellView[];
}

export interface MatrixLegendEntry {
  readonly state: MatrixCellState;
  /** The mock's own word, printed on the key beside the caption — „baza", „pewne", „~predykcja". */
  readonly label: string;
  /**
   * The same state as a sentence. It is what a cell's `aria-label` says and what the key's `title`
   * carries, because a one-word key is readable beside the grid it explains and useless read out on
   * its own.
   */
  readonly description: string;
}

export interface ProgressionMatrixView {
  /** "Przebieg fazy · T1–T4". */
  readonly title: string;
  /** What the whole grid is doing — the canon's `.pmx-s`. */
  readonly subtitle: string;
  readonly columns: readonly MatrixColumnView[];
  readonly rows: readonly MatrixRowView[];
  readonly legend: readonly MatrixLegendEntry[];
  /**
   * Whether this is the LIVE variant, a fork somebody is training. It widens the cycle track for
   * the split cells and reserves the „TU JEST KLIENT" tag's room, and it is NOT „a now column
   * exists": a phase the client is already past has lived cells and no now column in it.
   */
  readonly isLive: boolean;
  /** The phase-scope note. The matrix never crosses a phase boundary and says so. */
  readonly note: string;
  /**
   * The column the client is standing in, as a POSITION in `columns` plus the mock's own tag — or
   * `null` on a template, on a plan that has run out, and on an exercise whose lifespan skips the
   * open cycle.
   *
   * A position rather than a cycle index, because what reads it is a grid track: the marker is
   * placed on `columns[position]`'s own column and the two can never drift apart the way a `calc()`
   * over track widths would.
   */
  readonly nowColumn: {
    readonly position: number;
    readonly tag: string;
    /**
     * Where the tag sits over its column. It is CENTRED everywhere except the two ends, and the two
     * ends are the reason it exists: the tag is wider than a cycle track, so centred over the first
     * or the last column it hangs past the grid — and the scroller clips what hangs off its start
     * outright, because nothing can scroll there. Measured on a 12-cycle fork: „TU JEST KLIEN".
     */
    readonly align: 'start' | 'centre' | 'end';
  } | null;
}

export interface MatrixViewInput {
  readonly exercise: ExerciseDraft;
  /**
   * The cycles this exercise is actually prescribed in — its LIFESPAN, holes and all, never a start
   * and a count. The grid draws one column per entry and each cell's projection offset is its INDEX
   * here, because progression counts exposures rather than elapsed cycles.
   */
  readonly lifespan: CycleLifespan;
  /** Cycles 1..this are consumed by the client and locked. Zero outside a live fork. */
  readonly consumedCycles: number;
  /**
   * The column the surface is SHOWING, or omitted where it shows the exercise's own opening one.
   *
   * It arrived on 15 September 2026, on the owner's call, and it replaces the rule that said an axis
   * may never reach this function. That rule existed for one reason — a pin addressed on a column the
   * grid was not drawing — and the fix for that is `MatrixCellAddress.field`, which carries the same
   * answer down to the writer. With both halves the grid can follow the sheet's column chips, which
   * is what the mock has always drawn.
   *
   * A column the exercise does not measure, or one the wire holds as text, has no axis; the
   * exercise's own is the fallback rather than an empty grid.
   */
  readonly field?: MeasurementFieldKey;
  /**
   * What the client has already been given at this slot and what they did with it —
   * `executionsToMatrixInputs`' answer, or `null` / omitted for the TEMPLATE variant.
   *
   * It is the one input that turns this grid into the mock's live one: the done / now column heads,
   * the „TU JEST KLIENT" marker and the split cells all hang off it, and nothing here judges any of
   * it. `consumedCycles` stays the lock's own coordinate and is deliberately separate — the two
   * answer different questions (`livePlanLocks.ts`), and a paused fork has consumed cycles with no
   * open one.
   */
  readonly live?: LiveMatrixInput | null;
  readonly t: Translate;
  readonly locale: string;
}

/** The badge letter and kind per wire set type. `raw` covers an inbound type this build never authors. */
const BADGE: Readonly<Record<string, { readonly letter: string; readonly kind: string }>> = {
  Warmup: { letter: 'R', kind: 'warmup' },
  Working: { letter: 'W', kind: 'working' },
  Top: { letter: 'T', kind: 'top' },
  Backoff: { letter: 'B', kind: 'backoff' },
  AMRAP: { letter: 'A', kind: 'amrap' },
};

const RAW_BADGE = { letter: '·', kind: 'raw' } as const;

/**
 * A number, in the reader's own notation. Polish writes "2,5" where English writes "2.5", and every
 * number this feature prints goes through here — the matrix's cells, the bar's steppers, the
 * computed sentence and the pill.
 *
 * It is exported because the RULE BAR needs it too, and interpolating a raw `number` into an i18n
 * string is exactly how a decimal comma turns into a full stop halfway down one surface. Found by
 * driving the app: the pill said "+2,5 kg" and the sentence beside it said "2.5 kg".
 */
export function formatNumber(value: number, locale: string, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);
}

/** The signed delta annotation — "+2,5", "−1". The minus is U+2212, not a hyphen. */
export function formatSigned(value: number, locale: string): string {
  const formatted = formatNumber(Math.abs(value), locale);
  if (value === 0) return formatted;
  return `${value > 0 ? '+' : '−'}${formatted}`;
}

/** The axis's own unit suffix, or `null` where the number stands alone (RPE, RIR). */
function axisSuffix(axis: ProgressionAxis, t: Translate): string | null {
  if (axis.kind === 'weight') return t(`${KEY}.unit.kg`);
  if (axis.kind === 'percent') return t(`${KEY}.unit.percentOfMax`);
  return null;
}

/**
 * A value on the axis, scaled to display — as the bare figure and as the suffixed one. `'—'` for an
 * absent value, in both readings, because a missing number has no unit either.
 */
function formatAxisValue(
  valueRaw: number | null,
  axis: ProgressionAxis,
  t: Translate,
  locale: string,
): { readonly text: string; readonly value: string } {
  if (valueRaw == null) {
    const empty = t(`${KEY}.cell.empty`);
    return { text: empty, value: empty };
  }
  const scaled = valueRaw * axis.displayScale;
  const digits = axis.kind === 'percent' ? 1 : 2;
  const suffix = axisSuffix(axis, t);
  const number = formatNumber(scaled, locale, digits);
  return { text: suffix === null ? number : `${number} ${suffix}`, value: number };
}

/**
 * The whole matrix, worded and formatted. One call per render of one exercise's grid.
 *
 * **The axis is the caller's COLUMN, resolved here, and the exercise's own where none is given.**
 * It was derived from the exercise alone until 15 September 2026, because a surface previewing an
 * unapplied rule once handed its draft axis in and every cell the trainer then pinned was addressed
 * on a column the plan does not progress. The pin was the defect, and the pin is where it is fixed:
 * `MatrixCellAddress` carries the same column down to the one writer, so the grid and the pin can
 * never again be about different fields. What the grid shows is then free to be what the trainer
 * selected, which is the mock's own reading and the owner's.
 */
export function progressionMatrixView(input: MatrixViewInput): ProgressionMatrixView {
  const { exercise, lifespan, consumedCycles, t, locale } = input;
  const live = input.live ?? null;
  const axis =
    (input.field === undefined
      ? null
      : axisForField(input.field, exercise.prescriptionMode)) ?? axisForExercise(exercise);
  const rows = buildMatrix(exercise, lifespan, axis);
  const dynamic = resolvesInSession(exercise, axis);
  const topWeightByCycle = topWeightsOf(rows, exercise, axis);

  const columns = (rows[0]?.cells ?? []).map((cell) =>
    columnView(cell.cycleIndex, cell.cycleOffset === 0, consumedCycles, live, t),
  );

  const rowViews = rows.map((row) =>
    rowView({ row, exercise, axis, dynamic, consumedCycles, live, topWeightByCycle, t, locale }),
  );

  const nowPosition = columns.findIndex((column) => column.isNow);

  return {
    title: t(`${KEY}.matrix.title`, {
      from: columns[0]?.label ?? '',
      to: columns[columns.length - 1]?.label ?? '',
    }),
    subtitle: t(`${KEY}.matrix.subtitle.${matrixSubtitleKind(exercise, axis, dynamic)}`),
    columns,
    rows: rowViews,
    legend: MATRIX_CELL_STATES.map((state) => ({
      state,
      label: t(`${KEY}.legendShort.${state}`),
      description: t(`${KEY}.legend.${state}`),
    })),
    note: t(`${KEY}.matrix.phaseNote`),
    isLive: live !== null,
    nowColumn:
      nowPosition === -1
        ? null
        : {
            position: nowPosition,
            tag: t(`${KEY}.column.nowTag`),
            align: nowTagAlign(nowPosition, columns.length),
          },
  };
}

/**
 * Where the „TU JEST KLIENT" tag sits over its column — centred everywhere except the two ends. See
 * `ProgressionMatrixView.nowColumn.align` for why the ends are the reason it exists.
 */
function nowTagAlign(position: number, columnCount: number): 'start' | 'centre' | 'end' {
  if (position === 0) return 'start';
  if (position === columnCount - 1) return 'end';
  return 'centre';
}

/**
 * One cycle head — the number, the mock's sub-line under it, and the label the CHART prints.
 *
 * The three readings of the sub-line are the mock's own `cycleHeads(nowIdx)`, positions turned into
 * cycle indices: the authored column says „BAZA" even when the client is past it, a cycle before the
 * open one says „ZROBIONY", and the open one says „TERAZ". Everything ahead says nothing.
 *
 * The CHART's suffix is deliberately narrower and lower-case, because the mock's plot suffixes only
 * two columns (`'C' + n + (i === 0 ? ' · baza' : i === NOW - 1 ? ' · teraz' : '')`). On a template
 * there is no client, so both the sub-line past „BAZA" and the chart's suffix are absent — which is
 * this surface's long-standing template variant, unchanged.
 */
function columnView(
  cycleIndex: number,
  isSource: boolean,
  consumedCycles: number,
  live: LiveMatrixInput | null,
  t: Translate,
): MatrixColumnView {
  const label = t(`${KEY}.column.label`, { index: cycleIndex });
  const isNow = live !== null && live.nowCycle === cycleIndex;
  const isDone = live !== null && live.nowCycle !== null && cycleIndex < live.nowCycle;

  let suffix: 'source' | 'now' | 'done' | null = null;
  if (isSource) suffix = 'source';
  else if (isNow) suffix = 'now';
  else if (isDone) suffix = 'done';

  return {
    cycleIndex,
    label,
    isSource,
    isLocked: cycleIsClosed(live, consumedCycles, cycleIndex),
    subLabel: suffix === null ? null : t(`${KEY}.column.${suffix}`),
    isNow,
    halves:
      live !== null && live.verdicts.has(cycleIndex)
        ? {
            prescribed: t(`${KEY}.column.halfPrescribed`),
            performed: t(`${KEY}.column.halfPerformed`),
          }
        : null,
    chartLabel:
      live === null || (!isSource && !isNow)
        ? label
        : t(`${KEY}.column.chartLabel`, { label, suffix: t(`${KEY}.column.${isNow ? 'now' : 'source'}`) }),
  };
}


/**
 * The five ways a consumed cell has no performance to print.
 *
 * `nothing` is the finished occurrence carrying no number on the viewed column — the client did
 * train, they simply logged nothing here, and it is deliberately a different word from the four
 * that mean nobody trained at all.
 */
export type AbsenceKind = 'missed' | 'skipped' | 'cancelled' | 'inProgress' | 'nothing';

/**
 * What the client was given in this cell and what they did with it, or `null` where the question
 * does not arise — a template, and every cycle the client has not reached.
 *
 * It judges nothing: `verdict` is the backend's reading of the cycle and `shortfall` is its list of
 * columns. What this decides is only how to WORD them.
 */
function loggedView(
  cell: MatrixCell,
  exercise: ExerciseDraft,
  axis: ProgressionAxis,
  live: LiveMatrixInput | null,
  t: Translate,
  locale: string,
): LoggedCellView | null {
  if (live === null) return null;
  const entry: LoggedCell | undefined = live.logged.get(
    loggedCellKey(cell.cycleIndex, cell.setIndex),
  );
  if (entry === undefined) return null;

  /* NOT `?? 'short'`. A cell whose cycle carries no verdict is a cell nothing was claimed about, and
   * the honest drawing of that is no claim. Inventing the refusal here would put a red ✗ on the
   * sheet under a wire that said nothing. */
  const verdict = live.verdicts.get(cell.cycleIndex) ?? 'unjudged';
  /* A MISSED OR SKIPPED SESSION IS A FAILURE, and a cancelled one is nobody's. Owner's call,
   * 15 September 2026, which reverses the earlier „a cycle nobody trained is neutral" for the two
   * statuses that are the client's own doing. Still read off `status` and never off a number. */
  const failedToAttend = verdict === 'missed' || verdict === 'skipped';
  const trained = !failedToAttend && verdict !== 'cancelled' && verdict !== 'inProgress';

  const performed = trained
    ? halfOf({ values: entry.actual, ceiling: null, amrap: false }, exercise, axis, t, locale)
    : null;
  /* ALL FOUR conditions: an untrained row never reaches the shortfall reading, because what it failed
   * is the session and not a column, its tone is `missed` below, and it names no column. */
  const judged = countsAsWork(entry.setType) && trained && verdict !== 'unjudged';
  const absence = performed === null ? absenceKindOf(verdict) : null;

  let tone: LoggedCellView['tone'] = 'neutral';
  if (judged) tone = !entry.done || entry.shortfall.size > 0 ? 'short' : 'delivered';
  // A warm-up in a missed session was never measured, so it stays out of the failure too.
  else if (failedToAttend && countsAsWork(entry.setType)) tone = 'missed';

  const ceilingField = bandColumnOf(exercise);
  const markOf = (field: MeasurementFieldKey | null): LoggedLineMark | null => {
    if (field === null) return null;
    const logged = entry.actual[field];
    const target =
      field === ceilingField && entry.ceiling !== null ? entry.ceiling : entry.prescribed[field];
    let direction: LoggedLineMark['direction'] = null;
    if (logged !== undefined && target !== undefined) {
      if (logged > target) direction = 'above';
      else if (logged < target) direction = 'below';
      else direction = 'hit';
    }
    return { direction, isShort: judged && entry.shortfall.has(field) };
  };
  const valueMark = performed?.value == null ? null : markOf(axis.field);
  const restMark = performed === null ? null : markOf(performed.restField);
  const rowShortMark =
    tone === 'short' && valueMark?.isShort !== true && restMark?.isShort !== true;

  return {
    prescribed: halfOf(
      {
        values: entry.prescribed,
        ceiling: entry.ceiling,
        amrap: isAmrapSet({ setType: entry.setType, prescribedPercent: null }),
      },
      exercise,
      axis,
      t,
      locale,
    ) ?? {
      value: null,
      rest: null,
      restField: null,
      valueSpoken: null,
      others: null,
      row: t(`${KEY}.cell.empty`),
    },
    performed,
    /* „w trakcie", „odwołany" and „—" are three statements: the client is in it now, the session was
     * called off, and they trained but logged nothing here. A missed session says nothing at all. */
    word: absence === null || failedToAttend ? null : t(`${KEY}.cell.lived.${absence}`),
    tone,
    valueMark,
    restMark,
    rowShortMark,
    verdict,
    absence,
    shortColumns: judged
      ? [...entry.shortfall].map((field) => t(`${KEY}.cell.shortColumn.${field}`))
      : [],
  };
}

/**
 * One half of a lived cell, or null where the row carries no number at all.
 *
 * The large line is the grid's own column as a bare number, as an unlived cell prints it, and the
 * small line is ONE column through `setRowText`: the band's gate where the row prices it, otherwise
 * the first other column it prices in the dictionary's order, which is the plot's own pair
 * (`trajectoryGateOf`) read off this row. RPE and the rest stay on `row`, for the title and the name.
 */
function halfOf(
  row: SetRowValues,
  exercise: ExerciseDraft,
  axis: ProgressionAxis,
  t: Translate,
  locale: string,
): LoggedHalfView | null {
  const options = { fields: exercise.measurementFields, ceilingField: bandColumnOf(exercise), t, locale };
  const whole = setRowText(row, options);
  if (whole === null) return null;
  const onAxis = axis.field === null ? undefined : row.values[axis.field];
  if (axis.field === null || onAxis === undefined) {
    return { value: null, rest: whole, restField: null, valueSpoken: null, others: null, row: whole };
  }
  const priced = (field: MeasurementFieldKey) =>
    field !== axis.field && row.values[field] !== undefined;
  const pair =
    options.ceilingField !== null && priced(options.ceilingField)
      ? options.ceilingField
      : exercise.measurementFields.find(priced);
  const value = formatNumber(onAxis, locale);
  const { unit } = measurementFieldDefinition(axis.field);
  return {
    value,
    rest: pair === undefined ? null : setRowText(row, { ...options, fields: [pair] }),
    restField: pair ?? null,
    valueSpoken: unit === null ? value : `${value} ${unit}`,
    others: setRowText(row, {
      ...options,
      fields: options.fields.filter((field) => field !== axis.field && field !== pair),
    }),
    row: whole,
  };
}

/**
 * The word a cell prints where there is no performance to print.
 *
 * A FINISHED occurrence carrying nothing on the viewed column falls through to „w trakcie"'s
 * neighbour — the empty mark — because the client did train, they simply logged nothing here.
 *
 * Exported because the PLOT captions an untrained cycle with the same vocabulary, and one absence
 * worded in two places is two places for „bez treningu" and „pominięty" to drift apart.
 */
export function absenceKindOf(verdict: CycleVerdict | null): AbsenceKind {
  if (
    verdict === 'missed' ||
    verdict === 'skipped' ||
    verdict === 'cancelled' ||
    verdict === 'inProgress'
  ) {
    return verdict;
  }
  return 'nothing';
}

/** The clause each tone adds to a lived cell's name, or none where another clause carries it. */
const VERDICT_WORD: Readonly<Record<LoggedCellView['tone'], string | null>> = {
  delivered: 'deliveredWord',
  neutral: 'noVerdictWord',
  // A short row needs no word of its own: the shortfall clause names the column that held it back.
  short: null,
  // Nor does a missed one: it is read out as an absence, „nie wykonano".
  missed: null,
};

/**
 * A consumed cell, read out loud — in two sentences, because a cell holding a performance and a cell
 * holding an absence are two different statements:
 *
 * · „Seria 2, cykl 3: zadane 100 kg × 8, wykonane 100 kg × 6, brak na powtórzeniach."
 * · „Seria 2, cykl 3: zadane 100 kg × 8, nie wykonano."
 *
 * Colour is never the only channel, and on this cell it carries the verdict, the ✓ / ✗ and the split
 * itself. All three are said here in words, including the DELIVERED one: a green value with nothing
 * said about it is a verdict carried by hue alone.
 */
function livedAria(cell: MatrixCell, logged: LoggedCellView, t: Translate): string {
  const where = { set: cell.setIndex, cycle: cell.cycleIndex, prescribed: logged.prescribed.row };
  if (logged.performed === null) {
    return t(`${KEY}.cell.lived.ariaAbsent`, {
      ...where,
      absence: t(`${KEY}.cell.lived.absent.${logged.absence ?? 'nothing'}`),
    });
  }
  const verdictWord = VERDICT_WORD[logged.tone];
  return t(`${KEY}.cell.lived.aria`, {
    ...where,
    done: performedSpoken(logged, t),
    /* THREE TONES, THREE SENTENCES. A row carrying numbers the backend declined to judge read exactly
     * like a delivered one with the colour as the only difference. A short row needs no word here;
     * the shortfall clause below names the column. */
    verdict: verdictWord === null ? '' : t(`${KEY}.cell.lived.${verdictWord}`),
    shortfall:
      logged.shortColumns.length === 0
        ? ''
        : t(`${KEY}.cell.lived.shortOn`, { columns: logged.shortColumns.join(', ') }),
  })
    .replace(/\s+,/g, ',')
    .trim();
}

/**
 * The WYKONANE half read out: each printed line with its direction, „142,5 kg, powyżej; × 3", then
 * whatever else the row logged. The whole row where the half prints no split.
 */
function performedSpoken(logged: LoggedCellView, t: Translate): string {
  const { performed, valueMark, restMark } = logged;
  if (performed === null) return '';
  const said = (text: string, mark: LoggedLineMark | null): string =>
    mark?.direction === 'above' || mark?.direction === 'below'
      ? t(`${KEY}.cell.lived.direction.${mark.direction}`, { value: text })
      : text;
  if (performed.value === null || performed.valueSpoken === null) return performed.row;
  const lines = [said(performed.valueSpoken, valueMark)];
  if (performed.rest !== null) lines.push(said(performed.rest, restMark));
  if (performed.others !== null) lines.push(performed.others);
  return lines.join('; ');
}

/** What the grid as a whole is doing — three readings, and only one of them promises numbers. */
function matrixSubtitleKind(exercise: ExerciseDraft, axis: ProgressionAxis, dynamic: boolean): string {
  if (dynamic) return 'dynamic';
  const delta = ruleDelta(ruleFor(exercise.progressionRules, axis.field));
  if (delta === null && exercise.progressionOverrides.length > 0) return 'manual';
  if (delta === null) return 'flat';
  return 'certain';
}

/**
 * The kilograms each cycle's TOP set carries, for the descent estimate — or an empty map when there
 * is nothing to derive from.
 *
 * Only on the kilogram axis, because that is the only axis whose cells ARE kilograms: a %1RM top
 * holds a fraction and an effort top holds an RPE, and multiplying either by a descent's percent
 * produces a number that means nothing. The top's own cell value is used whatever produced it — a
 * static projection, a flat dynamic stand-in, or a pinned override — because the estimate is drawn
 * as an estimate and the `~` is the channel that says so.
 */
function topWeightsOf(
  rows: readonly MatrixRow[],
  exercise: ExerciseDraft,
  axis: ProgressionAxis,
): ReadonlyMap<number, number> {
  if (axis.kind !== 'weight') return new Map();
  const topIndex = exercise.sets.findIndex((set) => set.setType === 'Top');
  const topRow = topIndex === -1 ? undefined : rows[topIndex];
  if (topRow === undefined) return new Map();
  const weights = new Map<number, number>();
  for (const cell of topRow.cells) {
    if (cell.valueRaw != null) weights.set(cell.cycleIndex, cell.valueRaw);
  }
  return weights;
}

interface RowViewInput {
  readonly row: MatrixRow;
  readonly exercise: ExerciseDraft;
  readonly axis: ProgressionAxis;
  readonly dynamic: boolean;
  readonly consumedCycles: number;
  readonly live: LiveMatrixInput | null;
  readonly topWeightByCycle: ReadonlyMap<number, number>;
  readonly t: Translate;
  readonly locale: string;
}

function rowView(input: RowViewInput): MatrixRowView {
  const { row, exercise, t } = input;
  const set = exercise.sets[row.setIndex - 1];
  const setType = set?.setType ?? null;
  const badge = (setType !== null && BADGE[setType]) || RAW_BADGE;

  return {
    setIndex: row.setIndex,
    badge: {
      letter: badge.letter,
      kind: badge.kind,
      label: t(`${KEY}.badge.${badge.kind}`),
    },
    label: t(`${KEY}.row.label`, {
      index: row.setIndex,
      type: t(`${KEY}.badge.${badge.kind}`),
    }),
    cells: row.cells.map((cell) => cellView(cell, row, input)),
  };
}

function cellView(cell: MatrixCell, row: MatrixRow, input: RowViewInput): MatrixCellView {
  const { exercise, axis, dynamic, consumedCycles, live, topWeightByCycle, t, locale } = input;
  const set = exercise.sets[row.setIndex - 1];
  const isLocked = cycleIsClosed(live, consumedCycles, cell.cycleIndex);
  const isBase = cell.cycleOffset === 0;
  const state = cellState({ cell, row, exercise, axis, dynamic, isBase });
  /* THE BASE COLUMN TAKES AN EDITOR TOO, since 15 September 2026 — see `isEditable`'s own note and
   * `setMatrixBaseValue`. What it writes is different (the row's prescription, not a pin), which is
   * the commit path's business rather than this flag's. */
  const logged = loggedView(cell, exercise, axis, live, t, locale);
  /* A CYCLE THE CLIENT HAS ALREADY BEEN HANDED TAKES NO EDITOR, and that is a separate refusal from
   * the lock. The two boundaries are different questions (`livePlanLocks.ts`): a paused fork's
   * past-due cycles come back as `Missed` occurrences OUTSIDE `consumedCycles`, so such a cell was
   * unlocked, editable — and drew the split reading anyway, because the split branch renders first.
   * The press armed the editor, nothing appeared, and the Escape meant for a field that was never
   * there closed the whole sheet with the staged rule in it. */
  const isEditable =
    !isLocked && logged === null && !row.isWarmup && set !== undefined && countsAsWork(set.setType);
  /* ORDERED, because a cell can be refused three times over and only one reason is worth saying: a
   * locked cycle is a fact about the plan's boundary, a lived one is a fact about the client, and a
   * row that is not work is a fact about the row. */
  let refusalKey: 'locked' | 'lived' | 'notWork' = 'notWork';
  if (isLocked) refusalKey = 'locked';
  else if (logged !== null) refusalKey = 'lived';

  const { text, value, annotation } = cellText();

  return {
    key: `${String(cell.setIndex)}-${String(cell.cycleIndex)}`,
    setIndex: cell.setIndex,
    cycleIndex: cell.cycleIndex,
    state,
    isBase,
    isLocked,
    isEditable,
    editRefusal: isEditable ? null : t(`${KEY}.cell.refusal.${refusalKey}`),
    text,
    value,
    annotation,
    /* A LIVED cell states both sides and names the column that held the exercise back. The rule
     * states it is not the same sentence: the ordinary cell says what the plan asks and how certain
     * the number is, and neither of those is what a trainer reads a consumed cycle for. */
    ariaLabel:
      logged === null
        ? t(`${KEY}.cell.aria`, {
            value: text,
            state: t(`${KEY}.legend.${state}`),
            locked: isLocked ? t(`${KEY}.cell.locked`) : '',
          }).trim()
        : livedAria(cell, logged, t),
    editValueRaw: cell.valueRaw,
    editsLadderPercent: row.isBackoffDescent,
    // A ladder row edits a percent OF THE TOP whatever the exercise's axis is, so it scales by 100
    // there and by the axis's own factor everywhere else.
    editScale: row.isBackoffDescent ? 100 : axis.displayScale,
    // The estimate's kilograms are read back out of `text`; nothing downstream needs them apart.
    logged,
  };

  /** The cell's number in its two readings, and the sub-line under it. Closes over the cell. */
  function cellText(): {
    readonly text: string;
    readonly value: string;
    readonly annotation: string | null;
  } {
    // A ladder row's cells are a percent OF THE TOP on every axis, so they are formatted here rather
    // than through the axis — a descent under an RPE rule still shows "85 %", not "85 RPE".
    if (row.isBackoffDescent) {
      // A descent's sub-line LEADS with where its load comes from, then says what the client is
      // asked for in the other columns — the mock's „z topu · × 8–12".
      const fromTop = t(`${KEY}.cell.ofTop`);
      const prescription = otherColumns(set, exercise, axis, t, locale);
      const annotation = prescription === null ? fromTop : `${fromTop} · ${prescription}`;
      const percent = cell.valueRaw;
      if (percent == null) {
        const empty = t(`${KEY}.cell.empty`);
        return { text: empty, value: empty, annotation };
      }
      const percentNumber = formatNumber(percent * 100, locale, 1);
      const percentText = `${percentNumber} ${t(`${KEY}.unit.percent`)}`;
      const derived = deriveBackoffWeight(topWeightByCycle.get(cell.cycleIndex) ?? null, percent);
      if (derived == null) {
        return { text: percentText, value: percentNumber, annotation };
      }
      const derivedNumber = `~${formatNumber(derived, locale)}`;
      return {
        text: `${derivedNumber} ${t(`${KEY}.unit.kg`)}`,
        value: derivedNumber,
        annotation,
      };
    }

    const { text, value } = formatAxisValue(cell.valueRaw, axis, t, locale);
    return { text, value, annotation: otherColumns(set, exercise, axis, t, locale) };
  }
}

/** The sparse numbers of one set row, a draft set's, a frozen prescription's or a performance's. */
export interface SetRowValues {
  readonly values: Partial<Record<MeasurementFieldKey, number>>;
  /** The band's upper end on the banded column, or null where the row carries no band. */
  readonly ceiling: number | null;
  /** An AMRAP row's repetitions are a THRESHOLD, which the mock writes „5+". */
  readonly amrap: boolean;
}

export interface SetRowTextOptions {
  /** The exercise's own measurement contract: which columns exist, in the dictionary's order. */
  readonly fields: readonly MeasurementFieldKey[];
  /** The column `ceiling` belongs to, the band's gate, or null where nothing is banded. */
  readonly ceilingField: MeasurementFieldKey | null;
  /** A column already printed elsewhere in the same box, left out of this line. */
  readonly exclude?: MeasurementFieldKey | null;
  /**
   * A column's NUMBER already formatted by the caller, unit included, the plot's own axis scale.
   * The notation around it („× ", „RPE ≤ ") is still this function's.
   */
  readonly formatted?: Partial<Record<MeasurementFieldKey, string>>;
  readonly t: Translate;
  readonly locale: string;
}

/**
 * ONE set row as every box on this sheet prints it: the load, then „×" and the repetitions, then
 * every other column after „·", „102,5 kg × 5 · RPE ≤ 7,5", „60 kg × 8–12".
 *
 * ── THE ORDER IS THE ROW'S, NEVER THE SELECTED COLUMN'S ─────────────────────────────────────
 * Owner QA, 15 September 2026: with the Powt. chip selected the lived cell read „5102,5 kg · RPE ≤ 7,5"
 * while the Ciężar chip read „102,5 kg × 5 · RPE ≤ 7,5" for the same set. The line was assembled
 * around whichever column the grid followed, so the repetitions lost their „×" and were glued to the
 * load. A row says the same thing whichever chip is pressed, and this is the one place it is worded:
 * the unlived cell's sub-line, both halves of the lived cell and every label over the plot.
 *
 * `exclude` is how a box that already prints one of the columns large leaves it out of the small
 * line under it, and the order of what remains does not move. Without a load the repetitions keep
 * their „×" („× 8–12"), except an AMRAP threshold, which reads „5+" on its own.
 *
 * Returns null where the row prices nothing outside `exclude`.
 */
export function setRowText(row: SetRowValues, options: SetRowTextOptions): string | null {
  const { fields, ceilingField, exclude = null, formatted = {}, t, locale } = options;
  const spanOf = (field: MeasurementFieldKey): string | null => {
    if (field === exclude) return null;
    const ready = formatted[field];
    if (ready !== undefined) return ready;
    const value = row.values[field];
    if (value === undefined) return null;
    const number = formatNumber(value, locale);
    const ceiling = field === ceilingField ? row.ceiling : null;
    const span = ceiling === null ? number : `${number}–${formatNumber(ceiling, locale)}`;
    const { unit } = measurementFieldDefinition(field);
    return unit === null ? span : `${span} ${unit}`;
  };

  const load = fields.includes('weight') ? spanOf('weight') : null;
  const repsSpan = fields.includes('reps') ? spanOf('reps') : null;
  let reps: string | null = null;
  if (repsSpan !== null) {
    const threshold = row.amrap
      ? t(`${KEY}.cell.prescription.amrapReps`, { value: repsSpan })
      : repsSpan;
    reps =
      row.amrap && load === null
        ? threshold
        : t(`${KEY}.cell.prescription.reps`, { value: threshold });
  }

  const others = fields
    .filter((field) => field !== 'weight' && field !== 'reps')
    .map((field) => {
      const span = spanOf(field);
      if (span === null) return null;
      if (field === 'rpe' || field === 'rir') {
        return t(`${KEY}.cell.prescription.${field}`, { value: span });
      }
      return t(`${KEY}.cell.prescription.plain`, { value: span });
    })
    .filter((part): part is string => part !== null);

  const head = [load, reps].filter((part): part is string => part !== null).join(' ');
  const parts = head === '' ? others : [head, ...others];
  return parts.length === 0 ? null : parts.join(' · ');
}

/** A draft set as `setRowText`'s input, its own numbers, its band where one may sit, its role. */
function draftSetRow(set: SetDraft, fields: readonly MeasurementFieldKey[]): SetRowValues {
  const values: Partial<Record<MeasurementFieldKey, number>> = {};
  for (const field of fields) {
    const valueKey = measurementValueKey(field);
    const raw = valueKey === null ? undefined : set[valueKey];
    if (typeof raw === 'number') values[field] = raw;
  }
  // A warm-up never carries a ceiling, so a floor's pair on one would be a range nothing reads.
  return { values, ceiling: carriesBand(set) ? set.prescribedCeiling : null, amrap: isAmrapSet(set) };
}

/**
 * What the row prescribes in the columns this grid is NOT about, the mock's cell sub-line,
 * „× 8–12" under a load cell and „5+ · RPE ≤ 9" under an AMRAP's. `setRowText` with the axis left
 * out, because that number is already printed above it in the same cell.
 */
function otherColumns(
  set: SetDraft | undefined,
  exercise: ExerciseDraft,
  axis: ProgressionAxis,
  t: Translate,
  locale: string,
): string | null {
  if (set === undefined) return null;
  return setRowText(draftSetRow(set, exercise.measurementFields), {
    fields: exercise.measurementFields,
    ceilingField: bandColumnOf(exercise),
    exclude: axis.field,
    t,
    locale,
  });
}

/**
 * The column a set's `prescribedCeiling` belongs to: the band's gate, and repetitions where no rule
 * names one, the only column the set table has ever authored a ceiling on.
 */
export function bandColumnOf(exercise: Pick<ExerciseDraft, 'progressionRules'>): MeasurementFieldKey {
  return bandedGateOf(exercise.progressionRules) ?? 'reps';
}

/**
 * Which of the five a cell is.
 *
 * The order is the order the causes override each other: a pin wins over any rule, a warm-up never
 * progresses whatever the rule says, a ladder row's load arrives in session whatever the rule says,
 * and only then does the rule itself decide between certain and flat.
 */
function cellState(args: {
  readonly cell: MatrixCell;
  readonly row: MatrixRow;
  readonly exercise: ExerciseDraft;
  readonly axis: ProgressionAxis;
  readonly dynamic: boolean;
  readonly isBase: boolean;
}): MatrixCellState {
  const { cell, row, exercise, axis, dynamic, isBase } = args;

  if (!isBase && findOverride(exercise.progressionOverrides, cell.setIndex, cell.cycleIndex)) {
    // A ladder row only accepts a PERCENT pin, so an override carrying something else on such a row
    // is one the backend refuses and `buildMatrix` already ignored — it must not colour the cell.
    if (!row.isBackoffDescent || cell.kind === 'override') return 'manual';
  }
  // Flat whether or not it is the base column: `isBase` rides alongside as its own flag and carries
  // the ring, so the canon composes `s-base s-flat` rather than needing a state for the pair.
  if (row.isWarmup) return 'flat';
  // The descent's own fraction, not a projection of anything: its load resolves from the top set's
  // ACTUAL result in session, so there is no number here to be right or wrong about.
  if (row.isBackoffDescent) return 'derived';
  if (dynamic) return 'estimate';
  // A row carrying nothing on this axis has no value to move — an RPE rule over a set with no RPE,
  // a weight rule over a client-selected load. Flat is the honest reading, and it keeps the row out
  // of the green that would claim the engine computed something.
  if (cell.valueRaw == null) return 'flat';
  if (isBase) return 'base';
  const rule = ruleFor(exercise.progressionRules, axis.field);
  if (rule !== null && rule.type === 'Static' && rule.delta !== null) {
    // An effort rule with an off-grid delta still moves; a zero delta does not.
    return rule.delta === 0 && !isEffortAxis(axis) ? 'flat' : 'certain';
  }
  return 'flat';
}
